import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SeoHead from '../components/SeoHead';
import ProductCard from '../components/ProductCard';
import { getCmsCollection, getCmsPage } from '../lib/cms';

export default function JoseraPage() {
  const [joseraProducts, setJoseraProducts] = useState([]);
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;
    async function loadJosera() {
      try {
        const [res, pageData] = await Promise.all([
          getCmsCollection('produkter', { limit: 20 }),
          getCmsPage('/josera'),
        ]);

        if (isMounted) {
          if (pageData) setPage(pageData);
          // Filter products that contain JOSERA in name or description
          const filtered = (res.items || []).filter((p) =>
            p.navn?.toUpperCase().includes('JOSERA') ||
            p.kort_beskrivelse?.toUpperCase().includes('JOSERA') ||
            p.lang_beskrivelse?.toUpperCase().includes('JOSERA')
          );
          setJoseraProducts(filtered);
          setLoading(false);
        }
      } catch (err) {
        console.error('Fejl ved indlæsning af Josera produkter:', err);
        if (isMounted) setLoading(false);
      }
    }

    loadJosera();
    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <>
      <SeoHead
        title={page?.meta_title || "Josera Premium Foder - KROMIX A/S"}
        description={page?.meta_description || "Besøg også hjemmesiden med vores Premium foder Josera, som KROMIX er eneforhandler af i Danmark. Rigtig ernæring - sund levevis."}
      />

      {/* Hero Header */}
      <section className="bg-[#39463A] text-white py-14 sm:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl space-y-3">
            <span className="text-xs font-bold uppercase tracking-widest text-[#DED3C0] block">
              {page?.badge_tekst || 'Dansk samarbejde & distribution'}
            </span>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white">
              {page?.overskrift || 'Josera Premium Foder'}
            </h1>
            <p className="text-base sm:text-lg text-[#FCFAF5]/90 leading-relaxed">
              {page?.undertekst || 'Vi skaffer resultater – rigtig ernæring, sund levevis. KROMIX A/S distribuerer det anerkendte tyske premium foder Josera til hunde og katte i Danmark.'}
            </p>
            <div className="pt-2 flex flex-wrap gap-4">
              <a
                href="https://josera.com/da"
                target="_blank"
                rel="noopener noreferrer"
                className="px-6 py-3.5 rounded-xl bg-[#71806A] hover:bg-[#5C6A56] text-white text-xs font-bold uppercase tracking-wider transition-all shadow-md inline-flex items-center gap-2"
              >
                <span>Besøg Joseras officielle hjemmeside</span>
                <i className="fa-solid fa-arrow-up-right-from-square text-[11px]" aria-hidden="true" />
              </a>
              <Link
                to="/kontakt"
                className="px-6 py-3.5 rounded-xl bg-[#FCFAF5] hover:bg-white text-[#39463A] text-xs font-bold uppercase tracking-wider transition-all shadow-xs"
              >
                Kontakt os
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Content & Quality Section */}
      <section className="py-14 sm:py-20 bg-[#F4EFE5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-[#FCFAF5] rounded-3xl p-8 sm:p-12 border border-[#DED3C0] shadow-sm w-full space-y-8">
            <div className="space-y-4">
              <span className="text-xs font-bold uppercase tracking-wider text-[#71806A]">
                Samarbejdet
              </span>
              <h2 className="text-2xl sm:text-3xl font-bold text-[#39463A]">
                Kvalitetsfoder med passion for dyrevelfærd
              </h2>
              <div className="prose prose-stone text-sm text-[#39463A]/80 leading-relaxed space-y-3">
                <p>
                  <strong className="text-[#39463A]">Besøg også hjemmesiden med vores Premium foder Josera</strong>, som vi er eneforhandler af i Danmark.
                </p>
                <p>
                  Josera står for førsteklasses dyreernæring, hvor bæredygtighed, sunde ingredienser og højeste laboratoriekontrollerede standarder går hånd i hånd.
                  Gennem vores tætte distributionssamarbejde sikrer KROMIX, at danske kæledyrsejere og forhandlere har adgang til Joseras populære serier og fodertilbehør.
                </p>
              </div>
            </div>

            {/* Feature Highlights on Warm Oat with Sage Olive icons */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-4 border-t border-[#DED3C0]">
              <div className="p-4 rounded-xl bg-[#F4EFE5] border border-[#DED3C0] text-center">
                <i className="fa-solid fa-seedling text-xl text-[#71806A] mb-2 block" aria-hidden="true" />
                <h4 className="font-bold text-xs text-[#39463A]">Bæredygtig produktion</h4>
                <p className="text-[11px] text-[#39463A]/75 mt-1">Klimaneutral fremstilling med respekt for naturen.</p>
              </div>
              <div className="p-4 rounded-xl bg-[#F4EFE5] border border-[#DED3C0] text-center">
                <i className="fa-solid fa-microscope text-xl text-[#71806A] mb-2 block" aria-hidden="true" />
                <h4 className="font-bold text-xs text-[#39463A]">Højeste laboratoriestandard</h4>
                <p className="text-[11px] text-[#39463A]/75 mt-1">Løbende kontroller for optimal optagelighed.</p>
              </div>
              <div className="p-4 rounded-xl bg-[#F4EFE5] border border-[#DED3C0] text-center">
                <i className="fa-solid fa-handshake text-xl text-[#71806A] mb-2 block" aria-hidden="true" />
                <h4 className="font-bold text-xs text-[#39463A]">Dansk distribution</h4>
                <p className="text-[11px] text-[#39463A]/75 mt-1">Hurtig og sikker levering i hele Danmark via KROMIX.</p>
              </div>
            </div>
          </div>

          {/* Josera Products in KROMIX sortiment */}
          {joseraProducts.length > 0 && (
            <div className="mt-16">
              <div className="text-center max-w-2xl mx-auto mb-10">
                <span className="text-xs font-bold uppercase tracking-wider text-[#71806A]">
                  Sortiment
                </span>
                <h3 className="text-2xl sm:text-3xl font-bold text-[#39463A] mt-1">
                  Josera produkter hos KROMIX
                </h3>
                <p className="text-xs sm:text-sm text-[#39463A]/80 mt-1">
                  Se udvalgte Josera produkter tilgængelige gennem vores forhandlernetværk.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {joseraProducts.map((prod) => (
                  <ProductCard key={prod.id} product={prod} />
                ))}
              </div>
            </div>
          )}
        </div>
      </section>
    </>
  );
}
